import React, { useEffect, useState, createContext, useContext } from 'react';
// MDA Type
interface MDA {
  id: string;
  name: string;
  acronym: string;
}
interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: 'super_admin' | 'mda_admin' | 'moderator';
  permissions: string[];
  mda?: MDA; // MDA information for MDA admins
}
interface AdminAuthContextType {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  isSuperAdmin: () => boolean;
  getMdaId: () => string | null;
  getMdaName: () => string | null;
}
const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);
export const AdminAuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Check for saved admin on initial load
  useEffect(() => {
    const savedAdmin = localStorage.getItem('admin');
    if (savedAdmin) {
      setAdmin(JSON.parse(savedAdmin));
    }
    setIsLoading(false);
  }, []);
  const login = async (email: string, password: string): Promise<boolean> => {
    // Simulate API call for admin login
    setIsLoading(true);
    return new Promise(resolve => {
      setTimeout(() => {
        // For demo purposes only - in production this would validate against a backend
        if (email === 'admin@example.com' && password === 'admin123') {
          const mockAdmin: AdminUser = {
            id: 'admin-1',
            name: 'Admin User',
            email,
            role: 'super_admin',
            permissions: ['users:read', 'users:write', 'applications:read', 'applications:write', 'applications:approve', 'services:read', 'services:write', 'mdas:read', 'mdas:write', 'payments:read', 'payments:write', 'reports:read', 'settings:read', 'settings:write']
          };
          setAdmin(mockAdmin);
          localStorage.setItem('admin', JSON.stringify(mockAdmin));
          setIsLoading(false);
          resolve(true);
        } else if (email === 'superadmin@example.com' && password === 'super123') {
          const mockSuperAdmin: AdminUser = {
            id: 'admin-2',
            name: 'Super Admin',
            email,
            role: 'super_admin',
            permissions: ['users:read', 'users:write', 'users:delete', 'applications:read', 'applications:write', 'applications:approve', 'applications:delete', 'services:read', 'services:write', 'services:delete', 'mdas:read', 'mdas:write', 'mdas:delete', 'payments:read', 'payments:write', 'payments:refund', 'reports:read', 'reports:export', 'settings:read', 'settings:write']
          };
          setAdmin(mockSuperAdmin);
          localStorage.setItem('admin', JSON.stringify(mockSuperAdmin));
          setIsLoading(false);
          resolve(true);
        } else if (email === 'health@example.com' && password === 'health123') {
          // Ministry of Health Admin
          const mockMdaAdmin: AdminUser = {
            id: 'mda-admin-1',
            name: 'Health Admin',
            email,
            role: 'mda_admin',
            permissions: ['applications:read', 'applications:write', 'applications:approve', 'services:read', 'payments:read', 'reports:read'],
            mda: {
              id: '3',
              name: 'Ministry of Health',
              acronym: 'MOH'
            }
          };
          setAdmin(mockMdaAdmin);
          localStorage.setItem('admin', JSON.stringify(mockMdaAdmin));
          setIsLoading(false);
          resolve(true);
        } else if (email === 'education@example.com' && password === 'education123') {
          // Ministry of Education Admin
          const mockMdaAdmin: AdminUser = {
            id: 'mda-admin-2',
            name: 'Education Admin',
            email,
            role: 'mda_admin',
            permissions: ['applications:read', 'applications:write', 'applications:approve', 'services:read', 'payments:read', 'reports:read'],
            mda: {
              id: '2',
              name: 'Ministry of Education',
              acronym: 'MOE'
            }
          };
          setAdmin(mockMdaAdmin);
          localStorage.setItem('admin', JSON.stringify(mockMdaAdmin));
          setIsLoading(false);
          resolve(true);
        } else if (email === 'finance@example.com' && password === 'finance123') {
          // Ministry of Finance Admin
          const mockMdaAdmin: AdminUser = {
            id: 'mda-admin-3',
            name: 'Finance Admin',
            email,
            role: 'mda_admin',
            permissions: ['applications:read', 'applications:write', 'applications:approve', 'services:read', 'payments:read', 'reports:read'],
            mda: {
              id: '1',
              name: 'Ministry of Finance',
              acronym: 'MOF'
            }
          };
          setAdmin(mockMdaAdmin);
          localStorage.setItem('admin', JSON.stringify(mockMdaAdmin));
          setIsLoading(false);
          resolve(true);
        } else {
          setIsLoading(false);
          resolve(false);
        }
      }, 1000);
    });
  };
  const logout = () => {
    setAdmin(null);
    localStorage.removeItem('admin');
  };
  const hasPermission = (permission: string): boolean => {
    if (!admin) return false;
    return admin.permissions.includes(permission);
  };
  // Check if the current admin is a super admin
  const isSuperAdmin = (): boolean => {
    if (!admin) return false;
    return admin.role === 'super_admin';
  };
  // Get the MDA ID for the current admin
  const getMdaId = (): string | null => {
    if (!admin || !admin.mda) return null;
    return admin.mda.id;
  };
  // Get the MDA name for the current admin
  const getMdaName = (): string | null => {
    if (!admin || !admin.mda) return null;
    return admin.mda.name;
  };
  return <AdminAuthContext.Provider value={{
    admin,
    isAuthenticated: !!admin,
    isLoading,
    login,
    logout,
    hasPermission,
    isSuperAdmin,
    getMdaId,
    getMdaName
  }} data-id="element-815">
      {children}
    </AdminAuthContext.Provider>;
};
export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};